import { type CustomerImpact } from '@canvas/operational-types';

export type Space = {
  key: string;
  id: string;
};

export enum SiteEdition {
  PREMIUM = 'premium',
  FREE = 'free',
  STANDARD = 'standard',
}

export type Site = {
  hostname: string;
  cloudId: string;
  confluenceActivationId: string;
  standalone?: boolean;
  edition?: SiteEdition;
  /**
   * Decides if the site is shared with people who aren't a part of our team.
   *
   * If you set this to true, we won't filter this entire site out of our analytics.
   *
   * Defaults to INTERNAL_TEAM.
   *
   * @privateRemarks we re-use {@link CustomerImpact} since purpose of its existence is
   * almost identical and the values will likely be forever in sync.
   * Potentially we should call it CustomerAudience.
   */
  widestUsage?: CustomerImpact;
  /**
   * If the site is designed specifically for running automated testing then set
   * this to true.
   *
   * Ensure to opt out of TAP (onboarding) messages when meantForAutomatedTesting is true to avoid
   * onboarding popups from breaking automated tests.
   * @see https://post-office.atlassian.com/tap-opt-out for production sites
   * @see https://post-office-portal.stg-east.frontend.public.atl-paas.net/tap-opt-out for staging sites
   * @see https://developer.atlassian.com/platform/post-office/message-controls/tap-opt-out/ for more information
   *
   * This will make it so certain tools make sure to configure the site to be
   * optimized for automated testing. For example we disable temporary
   * spotlights for _all_ users for maximum stability and to ensure no one
   * sees something (spotlight in this case) that bots normally wouldn't see.
   */
  meantForAutomatedTesting?: boolean;
  /**
   * If we don't use this anymore, mark this as true.
   *
   * This will save us the hassle of attempting clean up spaces in these sites.
   *
   * Why would we still want to reference unused sites?
   *
   * A good example is analytics: We still want to make sure we ignore any sites
   * that we've previously used for testing in analytics queries.
   *
   * @default false
   */
  unused?: boolean;
  testConfig?: {
    fixedSpace?: Space;
  };
};
